import instantsearch from 'instantsearch.js';
import {
  configure,
  hits,
  clearRefinements,
  refinementList,
  stats,
  pagination,
} from 'instantsearch.js/es/widgets';
import searchClient from './searchClient';
import SearchPage from './search-page';

const containers = {
  stats: '#pathways-search-stats',
  hitsContainer: '#pathways-search-hits-container',
  hits: '#pathways-search-hits',
  pagination: '#pathways-search-pagination',
  refinementListDepartments: '#pathways-search-refinement-list-department',
  refinementListStudentTypes: '#pathways-search-refinement-list-student-types',
  refinementListDegrees: '#people-search-refinement-list-degrees',
  refinementListResearchCenters: '#pathways-search-refinement-list-research-centers',
  refinementListTopics: '#pathways-search-refinement-list-topics',
  clearRefinements: '#pathways-search-clear-refinements',
};

class AlgoliaPathwaysSearch extends SearchPage {
  constructor(
    indexName,
    department = null,
    researchCenter = null,
    degree = null,
    studentType = null,
  ) {
    super();

    this.indexName = indexName;

    // Pre-filter parameters
    this.department = department;
    this.departmentFilter = this.department ? ` AND departments:"${this.department}"` : '';

    this.researchCenter = researchCenter;
    this.researchCenterFilter = this.researchCenter
      ? ` AND research_centers:"${this.researchCenter}"`
      : '';

    this.degree = degree;
    this.degreeFilter = this.degree ? ` AND degrees:"${this.degree}"` : '';

    this.studentType = studentType;
    this.studentTypeFilter = this.studentType ? ` AND student_types:"${this.studentType}"` : '';

    this.hitsPerPage = 24;

    this.search = instantsearch({
      indexName: this.indexName,
      routing: true,
      searchClient,
      // https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-searchfunction
      searchFunction: helper => {
        helper.search();
      },
    });

    this.initialize();
    this.search.start();
  }

  initialize() {
    // https://www.algolia.com/doc/api-reference/widgets/js/
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter the query with facet type of student
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: `(type:student)${this.departmentFilter}${this.researchCenterFilter}${this.degreeFilter}${this.studentTypeFilter} AND student_types:pathway`,
      }),

      // https://www.algolia.com/doc/api-reference/widgets/stats/js/
      stats({
        container: containers.stats,
        templates: {
          text: data => {
            let count = '';

            const pagination = document.querySelector(containers.pagination);
            this.toggleHide(pagination, data.nbHits <= this.hitsPerPage);

            if (data.hasManyResults) {
              count += `${data.nbHits} results found`;
            } else if (data.hasOneResult) {
              count += `1 result found`;
            } else {
              count += `<span class="search__stats-no-results">No results found.</span>`;
            }

            return `${count}`;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: containers.hits,
        escapeHTML: false,
        templates: {
          /* eslint-disable */
          item(hit) {
            return `
              <a href="${hit.url}" class="hit-link">
                ${
                  hit.headshot
                    ? `
                  <div class="hit-image">
                    <img class="" src="${hit.headshot}" alt="${hit.title}" />
                  </div>
                  `
                    : `
                  <div class="hit-placeholder-image-wrapper">
                    <div class="hit-placeholder-image">
                      <svg>
                        <use xlink:href="#ne-n"></use>
                      </svg>
                    </div>
                  </div>
                `
                }
                <h2 class="hit-title">
                  ${instantsearch.highlight({ attribute: 'title', hit })}
                </h2>
                ${
                  hit.areas_of_study ? `<p class="hit-area-of-study">${hit.areas_of_study}</p>` : ``
                }
              </a>
            `;
          },
          /* eslint-enable */
        },
        transformItems: items => {
          // Hide pagination and results if no results
          this.toggleHide(this.results, items.length < 1);
          if (window.innerWidth <= 768 && items.length > 0) {
            this.toastActive();
          }
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      pagination({
        container: containers.pagination,
        showFirst: false,
        showLast: false,
        scrollTo: '.js--pathways-search',
        templates: {
          previous: `
            <span class="visually-hidden">Previous page</span>
            <svg class="arrow prev">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
          next: `
            <span class="visually-hidden">Next page</span>
            <svg class="arrow next">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
        },
      }),

      // Student types
      refinementList({
        container: containers.refinementListStudentTypes,
        attribute: 'student_types',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListStudentTypes)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // School or Department
      refinementList({
        container: containers.refinementListDepartments,
        attribute: 'departments',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if department is specified
          const refList = document
            .querySelector(containers.refinementListDepartments)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.department || items.length < 1);
          return items;
        },
      }),

      // Degree
      refinementList({
        container: containers.refinementListDegrees,
        attribute: 'degrees',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if department is specified
          const refList = document
            .querySelector(containers.refinementListDegrees)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.degree || items.length < 1);
          return items;
        },
      }),

      // Research Centers
      refinementList({
        container: containers.refinementListResearchCenters,
        attribute: 'research_centers',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results or if research center is specified
          const refList = document
            .querySelector(containers.refinementListResearchCenters)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, this.researchCenter || items.length < 1);
          return items;
        },
      }),

      // Topics
      refinementList({
        container: containers.refinementListTopics,
        attribute: 'topics',
        operator: 'and',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListTopics)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/clear-refinements/js/
      clearRefinements({
        container: containers.clearRefinements,
        templates: {
          resetLabel: 'Clear filters',
        },
      }),
    ]);
  }

  static init() {
    new AlgoliaPathwaysSearch();
  }
}

export default AlgoliaPathwaysSearch;
